import { createFileRoute, Link, useNavigate } from "@tanstack/react-router";
import { useState } from "react";
import { useServerFn } from "@tanstack/react-start";
import { toast } from "sonner";
import { MessageCircle } from "lucide-react";
import { AuthShell } from "@/components/auth-shell";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { supabase } from "@/integrations/supabase/client";
import { resetWithRecoveryWord } from "@/lib/recovery.functions";
import { whatsappSupportLink, WHATSAPP_SUPPORT_LABEL } from "@/lib/username";

export const Route = createFileRoute("/sifre-unutdum")({
  head: () => ({
    meta: [
      { property: "og:type", content: "website" },
      { name: "twitter:card", content: "summary_large_image" },
      { title: "Şifrəni bərpa et — Albazar.az" },
      { name: "description", content: "Şifrənizi unutmusunuzsa, gizli söz ilə pulsuz və dərhal yeni şifrə yaradın." },
      { property: "og:title", content: "Şifrəni bərpa et — Albazar.az" },
      { property: "og:description", content: "Pulsuz və sürətli şifrə bərpası." },
      { name: "robots", content: "noindex, nofollow" },
    ],
  }),
  component: Forgot,
});

function Forgot() {
  const navigate = useNavigate();
  const reset = useServerFn(resetWithRecoveryWord);
  const [form, setForm] = useState({ identifier: "", word: "", password: "" });
  const [busy, setBusy] = useState(false);
  const set = (k: keyof typeof form) => (e: React.ChangeEvent<HTMLInputElement>) =>
    setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (form.password.length < 6) { toast.error("Şifrə ən azı 6 simvol olmalıdır"); return; }
    setBusy(true);
    try {
      const r = await reset({ data: form });
      if (!r.ok) {
        toast.error(r.error);
        return;
      }
      const { error } = await supabase.auth.signInWithPassword({ email: r.email, password: form.password });
      if (error) {
        toast.success("Şifrə yeniləndi. İndi daxil olun.");
        navigate({ to: "/giris", replace: true });
        return;
      }
      toast.success("Şifrəniz yeniləndi, xoş gəldiniz!");
      navigate({ to: "/profil", replace: true });
    } catch {
      toast.error("Xəta baş verdi, yenidən cəhd edin");
    } finally {
      setBusy(false);
    }
  };

  return (
    <AuthShell
      title="Şifrəni unutmusunuz?"
      subtitle="Telefon nömrənizi və gizli sözünüzü yazın, yeni şifrə yaradın — pulsuz və dərhal"
      footer={<Link to="/giris" className="font-semibold text-primary">Girişə qayıt</Link>}
    >
      <form className="space-y-4" onSubmit={submit}>
        <div>
          <Label htmlFor="id">Telefon nömrəsi və ya istifadəçi adı</Label>
          <Input id="id" inputMode="tel" required value={form.identifier} onChange={set("identifier")} className="mt-2 h-11 rounded-2xl" placeholder="050 123 45 67" />
        </div>
        <div>
          <Label htmlFor="w">Gizli söz</Label>
          <Input id="w" required value={form.word} onChange={set("word")} className="mt-2 h-11 rounded-2xl" />
        </div>
        <div>
          <Label htmlFor="np">Yeni şifrə</Label>
          <Input id="np" type="password" autoComplete="new-password" required value={form.password} onChange={set("password")} className="mt-2 h-11 rounded-2xl" placeholder="••••••••" />
        </div>
        <Button type="submit" disabled={busy} className="h-12 w-full rounded-2xl text-base">
          {busy ? "Gözləyin..." : "Şifrəni yenilə və daxil ol"}
        </Button>
      </form>

      <p className="text-center text-xs text-muted-foreground">Gizli söz təyin etməmisinizsə:</p>
      <a
        href={whatsappSupportLink("Salam, Albazar.az hesabımın şifrəsini unutmuşam. İstifadəçi adım: ")}
        target="_blank"
        rel="noopener noreferrer"
        className="flex h-12 w-full items-center justify-center gap-2 rounded-2xl border border-border bg-accent/40 text-base font-semibold transition-colors hover:bg-accent"
      >
        <MessageCircle className="size-5 text-info" />
        WhatsApp ilə yazın ({WHATSAPP_SUPPORT_LABEL})
      </a>
    </AuthShell>
  );
}
